// src/components/AddPaymentMethodForm.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio,
  Stack,
  Divider,
} from "@mui/material";
import { useAuthContext } from "../context/AuthContext";
import { useCompanies } from "../hooks/useCompanies";
import { useTypes } from "../hooks/useTypes";
import { fetchData } from "../api/client";

/** Tipos exatamente como no admin */
const TIPO_OPCOES = [
  { label: "Default", value: 0 },
  { label: "PIX Buspay", value: 1 },
  { label: "Pagar.me", value: 2 },
  { label: "Cielo", value: 3 },
  { label: "Pague Fácil", value: 4 },
  { label: "Dinheiro", value: 5 },
  { label: "Mercado Pago", value: 6 },
  { label: "Xpay", value: 7 },
  { label: "PIX Itaú", value: 8 },
  { label: "Rede", value: 9 },
  { label: "Adyen", value: 10 },
  { label: "AppMax — link / checkout", value: 11 },
  { label: "AppMax — PIX (API)", value: 12 },
  { label: "AppMax — cartão (API)", value: 13 },
];

/** Títulos amigáveis para as bases de status de pagamento */
const PAYMENT_BASE_TITLES = {
  pay_approved: "Pagamento aprovado",
  pay_pending: "Pagamento pendente",
  pay_refused: "Pagamento recusado",
};

/** Lista explícita das bases que queremos exibir (nome igual ao da API) */
const PAYMENT_BASES = ["pay_approved", "pay_pending", "pay_refused"];

/** Helper de título genérico */
const titleOf = (o) => o?.nome ?? o?.name ?? o?.label ?? `#${o?.id ?? ""}`;

/** Regras de obrigatoriedade/rotulagem por tipo */
function getFieldRulesByTipo(tipo) {
  const rules = {
    comuns: [
      "nome",
      "status",
      "tipo",
      "emitirbpe",
      "id_interno",
      "acrescimo",
      "taxa_retencao",
      "desconto",
      "parcelas_semjuros",
      "parcelas_comjuros",
      "juros_parcelamento",
    ],
    required: [],
    labels: {},
    helpers: {},
    show: new Set(),
  };
  rules.comuns.forEach((f) => rules.show.add(f));

  switch (Number(tipo)) {
    case 3: // Cielo (Checkout)
      rules.labels.client_id = "Client ID";
      rules.labels.client_secret = "Client Secret";
      rules.labels.urlapi = "Host da API (opcional)";
      rules.helpers.urlapi = "Ex.: apisandbox.cieloecommerce.cielo.com.br (sandbox)";
      rules.required.push("client_id", "client_secret");
      rules.show.add("client_id");
      rules.show.add("client_secret");
      rules.show.add("urlapi");
      break;

    case 6: // Mercado Pago
      rules.labels.client_id = "Public Key";
      rules.labels.client_secret = "Access Token";
      rules.required.push("client_id", "client_secret");
      rules.show.add("client_id");
      rules.show.add("client_secret");
      break;

    case 8: {
      // PIX Itaú
      rules.labels.client_id = "API Pix Client Id";
      rules.labels.client_secret = "API Pix Client Secret";
      rules.labels.pix_key = "Pix Key";
      rules.labels.pix_merchant_name = "Pix Merchant Name";
      rules.labels.pix_merchant_city = "Pix Merchant City";
      rules.labels.urlapi = "Base das APIs Pix (host)";
      rules.helpers.urlapi = "Ex.: https://secure.api.itau";
      rules.labels.pix_auth_url = "URL do OAuth (opcional)";
      rules.required.push(
        "client_id",
        "client_secret",
        "pix_key",
        "pix_merchant_name",
        "pix_merchant_city"
      );
      rules.show.add("client_id");
      rules.show.add("client_secret");
      rules.show.add("pix_key");
      rules.show.add("pix_merchant_name");
      rules.show.add("pix_merchant_city");
      rules.show.add("urlapi");
      rules.show.add("pix_auth_url");
      // TLS editor
      rules.show.add("pix_tls_mode");
      rules.show.add("pix_cert_pem");
      rules.show.add("pix_key_pem");
      rules.show.add("pix_ca_pem");
      rules.show.add("pix_pfx_b64");
      rules.show.add("pix_pfx_pass");
      break;
    }

    case 9: // Rede
      rules.labels.client_id = "Filiation";
      rules.labels.client_secret = "Token";
      rules.required.push("client_id", "client_secret");
      rules.show.add("client_id");
      rules.show.add("client_secret");
      break;

    case 10: // Adyen
      rules.labels.client_id = "API Key";
      rules.labels.client_secret = "Merchant Account";
      rules.labels.urlapi = "URL da API";
      rules.helpers.urlapi = "Ex.: https://pal-test.adyen.com (teste)";
      rules.required.push("client_id", "client_secret", "urlapi");
      rules.show.add("client_id");
      rules.show.add("client_secret");
      rules.show.add("urlapi");
      break;

    case 11: // AppMax — link
    case 12: // AppMax — PIX
    case 13: {
      // AppMax — API v3 (access-token no body)
      const t = Number(tipo);
      rules.labels.client_secret = "Chave API (access-token)";
      rules.labels.urlapi = "Base da API (ex.: https://homolog.sandboxappmax.com.br/api/v3)";
      const base =
        "Ex.: https://homolog.sandboxappmax.com.br/api/v3 — use a base do ambiente (homolog/produção) indicada pela Appmax.";
      rules.helpers.urlapi =
        t === 11
          ? `${base} Fluxo deste cadastro: link/checkout (customer + order).`
          : t === 12
            ? `${base} Fluxo deste cadastro: PIX via API (CPF do pagador na geração da cobrança).`
            : `${base} Fluxo deste cadastro: cartão via API (dados do cartão na requisição).`;
      rules.required.push("client_secret");
      rules.show.add("client_secret");
      rules.show.add("urlapi");
      break;
    }

    default:
      break;
  }

  return rules;
}

const numberOrNull = (v) => {
  if (v === "" || v === null || typeof v === "undefined") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function normalizeMultiline(s) {
  return String(s || "").replace(/\r\n/g, "\n");
}

const AddPaymentMethodForm = ({ paymentMethod, onClose, onSubmit, isLoading }) => {
  const { userData } = useAuthContext();

  // Carrega empresas/projetos para o select de Projeto
  const { data: companiesData, isLoading: loadingCompanies } = useCompanies(1, 500, "");
  const companies = companiesData?.companies || [];

  // Carrega árvore de tipos (para mapeamento de status de pagamento)
  const { data: typesData, isLoading: loadingTypes } = useTypes(1, 1000, "");
  const rootTypes = typesData?.types || [];

  const [errorMessage, setErrorMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingDetail, setIsFetchingDetail] = useState(false);

  const [formData, setFormData] = useState({
    ...paymentMethod,
    id_usuario: paymentMethod?.id_usuario ?? userData?.id ?? null,
    id_usuario_ultima_alteracao: userData?.id ?? null,
    // Projeto: 0/null => disponível em todos
    id_projeto:
      typeof paymentMethod?.id_projeto === "number" || typeof paymentMethod?.id_projeto === "string"
        ? Number(paymentMethod.id_projeto) || 0
        : 0,
    status: typeof paymentMethod?.status === "number" ? paymentMethod.status : 1,
    default: typeof paymentMethod?.default === "number" ? paymentMethod.default : 0,
    emitirbpe: typeof paymentMethod?.emitirbpe === "number" ? paymentMethod.emitirbpe : 0,
    tipo:
      typeof paymentMethod?.tipo === "number" || typeof paymentMethod?.tipo === "string"
        ? Number(paymentMethod.tipo)
        : 0,
    id_interno: paymentMethod?.id_interno ?? "",
    acrescimo: paymentMethod?.acrescimo ?? "",
    taxa_retencao: paymentMethod?.taxa_retencao ?? "",
    desconto: paymentMethod?.desconto ?? "",
    parcelas_semjuros: paymentMethod?.parcelas_semjuros ?? "",
    parcelas_comjuros: paymentMethod?.parcelas_comjuros ?? "",
    juros_parcelamento: paymentMethod?.juros_parcelamento ?? "",
    client_id: paymentMethod?.client_id ?? "",
    client_secret: paymentMethod?.client_secret ?? "",
    urlapi: paymentMethod?.urlapi ?? "",
    pix_auth_url: paymentMethod?.pix_auth_url ?? "",

    // PIX (business)
    pix_key: paymentMethod?.pix_key ?? "",
    pix_merchant_name: paymentMethod?.pix_merchant_name ?? "",
    pix_merchant_city: paymentMethod?.pix_merchant_city ?? "",

    // TLS (novos)
    pix_tls_mode: paymentMethod?.pix_tls_mode ?? "", // 'pem' | 'pfx'
    pix_cert_pem: paymentMethod?.pix_cert_pem ?? "",
    pix_key_pem: paymentMethod?.pix_key_pem ?? "",
    pix_ca_pem: paymentMethod?.pix_ca_pem ?? "",
    pix_pfx_b64: paymentMethod?.pix_pfx_b64 ?? "",
    pix_pfx_pass: paymentMethod?.pix_pfx_pass ?? "",

    // Mapeamento de tipos por status de pagamento (já no padrão da API)
    pay_approved_type_id: paymentMethod?.pay_approved_type_id ?? null,
    pay_approved_type1_id: paymentMethod?.pay_approved_type1_id ?? null,
    pay_approved_type2_id: paymentMethod?.pay_approved_type2_id ?? null,
    pay_approved_type3_id: paymentMethod?.pay_approved_type3_id ?? null,

    pay_pending_type_id: paymentMethod?.pay_pending_type_id ?? null,
    pay_pending_type1_id: paymentMethod?.pay_pending_type1_id ?? null,
    pay_pending_type2_id: paymentMethod?.pay_pending_type2_id ?? null,
    pay_pending_type3_id: paymentMethod?.pay_pending_type3_id ?? null,

    pay_refused_type_id: paymentMethod?.pay_refused_type_id ?? null,
    pay_refused_type1_id: paymentMethod?.pay_refused_type1_id ?? null,
    pay_refused_type2_id: paymentMethod?.pay_refused_type2_id ?? null,
    pay_refused_type3_id: paymentMethod?.pay_refused_type3_id ?? null,
  });

  /** Carrega o detalhe quando vier só { id } */
  useEffect(() => {
    const loadDetail = async () => {
      if (!paymentMethod?.id) return;
      setIsFetchingDetail(true);
      try {
        const resp = await fetchData(`/payment-methods/${paymentMethod.id}`, "GET");
        const m = resp?.data || {};
        setFormData((prev) => ({
          ...prev,
          ...m,
          // nunca reidrata segredos; flags has_* vêm em m e são mantidas em prev
          client_id: "",
          client_secret: "",
          pix_key: "",
          pix_cert_pem: "",
          pix_key_pem: "",
          pix_ca_pem: "",
          pix_pfx_b64: "",
          pix_pfx_pass: "",
          pix_auth_url: m.pix_auth_url ?? "",
          tipo: typeof m.tipo === "number" || typeof m.tipo === "string" ? Number(m.tipo) : 0,
          status: Number(m.status) === 1 ? 1 : 0,
          default: Number(m.default) === 1 ? 1 : 0,
          emitirbpe: Number(m.emitirbpe) === 1 ? 1 : 0,
          id_projeto:
            typeof m.id_projeto === "number" || typeof m.id_projeto === "string"
              ? Number(m.id_projeto) || 0
              : 0,
        }));
      } catch (e) {
        setErrorMessage("Não foi possível carregar os dados da forma de pagamento.");
      } finally {
        setIsFetchingDetail(false);
      }
    };
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod?.id]);

  const rules = useMemo(() => getFieldRulesByTipo(formData.tipo), [formData.tipo]);
  const labelFor = (field, fallback) => (rules.labels && rules.labels[field]) || fallback;

  const hasStoredSecret = (field) => !!formData[`has_${field}`];
  const isFieldRequired = (field) => rules.required.includes(field) && !hasStoredSecret(field);

  const maskSecretField = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: "",
      [`has_${field}`]: false,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: ["status", "default", "emitirbpe", "tipo", "id_projeto"].includes(name)
        ? Number(value)
        : value,
    }));
  };

  const handleFileToBase64 = async (file) => {
    const readAsDataURL = (f) =>
      new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(f);
      });
    const dataUrl = await readAsDataURL(file);
    const base64 = String(dataUrl).split(",")[1] || "";
    return base64;
  };

  /** ======= Helpers de cascata de tipos para status de pagamento ======= */
  const getCascadeLevels = (base) => {
    const rootVal = formData[`${base}_type_id`];
    const root = rootTypes.find((t) => Number(t.id) === Number(rootVal));
    const l1 = Array.isArray(root?.children) ? root.children : [];

    const sub1Val = formData[`${base}_type1_id`];
    const n1 = l1.find((s) => Number(s.id) === Number(sub1Val));
    const l2 = Array.isArray(n1?.children) ? n1.children : [];

    const sub2Val = formData[`${base}_type2_id`];
    const n2 = l2.find((u) => Number(u.id) === Number(sub2Val));
    const l3 = Array.isArray(n2?.children) ? n2.children : [];

    return { l1, l2, l3 };
  };

  const handleCascadeChange = (base, level, val) => {
    setFormData((prev) => {
      const next = { ...prev };
      if (level === 0) {
        next[`${base}_type_id`] = val === "" ? null : val;
        next[`${base}_type1_id`] = null;
        next[`${base}_type2_id`] = null;
        next[`${base}_type3_id`] = null;
      }
      if (level === 1) {
        next[`${base}_type1_id`] = val === "" ? null : val;
        next[`${base}_type2_id`] = null;
        next[`${base}_type3_id`] = null;
      }
      if (level === 2) {
        next[`${base}_type2_id`] = val === "" ? null : val;
        next[`${base}_type3_id`] = null;
      }
      if (level === 3) {
        next[`${base}_type3_id`] = val === "" ? null : val;
      }
      return next;
    });
  };

  const renderCascade = (base) => {
    const titulo = PAYMENT_BASE_TITLES[base] ?? base;
    const { l1, l2, l3 } = getCascadeLevels(base);

    return (
      <React.Fragment key={base}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            {titulo}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Tipo"
            value={formData[`${base}_type_id`] ?? ""}
            onChange={(e) => handleCascadeChange(base, 0, e.target.value)}
            disabled={loadingTypes}
          >
            <MenuItem value="">—</MenuItem>
            {!loadingTypes &&
              rootTypes.map((t) => (
                <MenuItem key={`${base}-root-${t.id}`} value={t.id}>
                  {titleOf(t)}
                </MenuItem>
              ))}
          </TextField>
        </Grid>

        {l1.length > 0 && (
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Subtipo 1"
              value={formData[`${base}_type1_id`] ?? ""}
              onChange={(e) => handleCascadeChange(base, 1, e.target.value)}
            >
              <MenuItem value="">—</MenuItem>
              {l1.map((s) => (
                <MenuItem key={`${base}-l1-${s.id}`} value={s.id}>
                  {titleOf(s)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        {l2.length > 0 && (
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Subtipo 2"
              value={formData[`${base}_type2_id`] ?? ""}
              onChange={(e) => handleCascadeChange(base, 2, e.target.value)}
            >
              <MenuItem value="">—</MenuItem>
              {l2.map((u) => (
                <MenuItem key={`${base}-l2-${u.id}`} value={u.id}>
                  {titleOf(u)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        {l3.length > 0 && (
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Subtipo 3"
              value={formData[`${base}_type3_id`] ?? ""}
              onChange={(e) => handleCascadeChange(base, 3, e.target.value)}
            >
              <MenuItem value="">—</MenuItem>
              {l3.map((v) => (
                <MenuItem key={`${base}-l3-${v.id}`} value={v.id}>
                  {titleOf(v)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
      </React.Fragment>
    );
  };

  /** Bases que realmente existem no formData (mantém flexível) */
  const cascadeBases = useMemo(
    () =>
      PAYMENT_BASES.filter((b) => Object.prototype.hasOwnProperty.call(formData, `${b}_type_id`)),
    [formData]
  );

  /** Validação com lista de erros (para bloquear o botão Salvar) */
  const errors = useMemo(() => {
    const list = [];
    const nome = String(formData.nome || "").trim();
    if (!nome || nome.length < 3) list.push("Informe um nome com ao menos 3 caracteres.");
    if (formData.tipo === null || typeof formData.tipo === "undefined") list.push("Selecione um tipo.");

    // obrigatórios por tipo (base)
    rules.required.forEach((f) => {
      const v = String(formData[f] ?? "").trim();
      // em edição, se já existir um valor armazenado no backend (has_* = true),
      // não exigimos que o usuário reenvie o segredo
      if (!v && !hasStoredSecret(f)) list.push(`Campo obrigatório: ${labelFor(f, f)}.`);
    });

    // mTLS PIX Itaú
    if (Number(formData.tipo) === 8 && Number(formData.status) === 1) {
      const mode = String(formData.pix_tls_mode || "").toLowerCase();
      const hasPemPair = !!formData.has_pix_cert_pem && !!formData.has_pix_key_pem;
      const hasPfxPair = !!formData.has_pix_pfx_b64 && !!formData.has_pix_pfx_pass;

      if (mode === "pem") {
        const hasTypedCert = String(formData.pix_cert_pem || "").trim() !== "";
        const hasTypedKey = String(formData.pix_key_pem || "").trim() !== "";
        if (!(hasTypedCert && hasTypedKey) && !hasPemPair) {
          list.push("PIX Itaú: informe Cert PEM e Key PEM.");
        }
      } else if (mode === "pfx") {
        const hasTypedFile = String(formData.pix_pfx_b64 || "").trim() !== "";
        const hasTypedPass = String(formData.pix_pfx_pass || "").trim() !== "";
        if (!(hasTypedFile && hasTypedPass) && !hasPfxPair) {
          list.push("PIX Itaú: envie o PFX (Base64) e a senha.");
        }
      } else {
        // só exige modo TLS se a forma estiver ativa e não houver nada salvo
        if (!hasPemPair && !hasPfxPair) {
          list.push("PIX Itaú: selecione o modo TLS (PEM ou PFX).");
        }
      }
    }
    return list;
  }, [formData, rules]);

  const canSubmit = errors.length === 0 && !isSubmitting && !isLoading && !isFetchingDetail;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      if (errors.length) throw new Error(errors.join(" "));

      const payload = {
        ...formData,
        // Normalizações principais
        nome: String(formData.nome || "").trim(),
        tipo: Number(formData.tipo),
        id_projeto:
          formData.id_projeto === 0 || formData.id_projeto == null
            ? 0 // 0/null => disponível em todos os projetos (mantemos 0 explícito para o FE e a API converte)
            : Number(formData.id_projeto),
        acrescimo: numberOrNull(formData.acrescimo),
        taxa_retencao: numberOrNull(formData.taxa_retencao),
        desconto: numberOrNull(formData.desconto),
        parcelas_semjuros: numberOrNull(formData.parcelas_semjuros),
        parcelas_comjuros: numberOrNull(formData.parcelas_comjuros),
        juros_parcelamento: numberOrNull(formData.juros_parcelamento),
        status: Number(formData.status) === 1 ? 1 : 0,
        default: Number(formData.default) === 1 ? 1 : 0,
        emitirbpe: Number(formData.emitirbpe) === 1 ? 1 : 0,
        id_interno: String(formData.id_interno || "").trim() || null,
        client_id: String(formData.client_id || "").trim() || null,
        client_secret: String(formData.client_secret || "").trim() || null,
        urlapi: String(formData.urlapi || "").trim() || null,
        pix_auth_url: String(formData.pix_auth_url || "").trim() || null,

        pix_key: String(formData.pix_key || "").trim() || null,
        pix_merchant_name: String(formData.pix_merchant_name || "").trim() || null,
        pix_merchant_city: String(formData.pix_merchant_city || "").trim() || null,

        // TLS – normaliza quebras de linha
        pix_tls_mode: String(formData.pix_tls_mode || "").trim() || null,
        pix_cert_pem: normalizeMultiline(formData.pix_cert_pem || "") || null,
        pix_key_pem: normalizeMultiline(formData.pix_key_pem || "") || null,
        pix_ca_pem: normalizeMultiline(formData.pix_ca_pem || "") || null,
        pix_pfx_b64: String(formData.pix_pfx_b64 || "").trim() || null,
        pix_pfx_pass: String(formData.pix_pfx_pass || "").trim() || null,

        // garantimos que os legados NÃO sejam usados
        detalhes1: null,
        detalhes2: null,
        detalhes3: null,
      };

      // Normaliza todos os campos *_type_id / *_type[1-3]_id (inclui pay_*_type_id)
      Object.keys(payload).forEach((k) => {
        if (/_type(?:[123])?_id$/.test(k)) {
          const v = payload[k];
          if (v === "" || v === null || typeof v === "undefined") {
            payload[k] = null;
          } else {
            const n = Number(v);
            payload[k] = Number.isFinite(n) ? n : null;
          }
        }
      });

      await onSubmit(payload);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || error?.message || "Erro ao salvar a forma de pagamento."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isPixItau = Number(formData.tipo) === 8;

  // Helper para exibir "Nome (ID X)"
  const projectDisplayName = (c) => {
    const name = c?.nome || c?.fantasia || c?.razao_social || `Projeto #${c?.id}`;
    return `${name} (ID ${c?.id})`;
  };

  return (
    <Paper elevation={3} sx={{ padding: 3, width: "95%", margin: "0 auto" }}>
      <Typography variant="h6" gutterBottom>
        {formData?.id ? "Editar Forma de Pagamento" : "Adicionar Forma de Pagamento"}
      </Typography>

      {(errorMessage || isFetchingDetail) && (
        <Box sx={{ mb: 2 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {isFetchingDetail && <Alert severity="info">Carregando dados…</Alert>}
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {/* Nome */}
          <Grid item xs={12}>
            <TextField
              label="Nome *"
              name="nome"
              value={formData.nome || ""}
              onChange={handleChange}
              fullWidth
              required
              inputProps={{ maxLength: 120 }}
              helperText="Ex.: PIX Itaú, Cartão de Crédito Cielo, Dinheiro no balcão"
            />
          </Grid>

          {/* Linha de selects: Projeto, Status, Tipo  */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Projeto</InputLabel>
              <Select
                name="id_projeto"
                label="Projeto"
                value={Number(formData.id_projeto) || 0}
                onChange={handleChange}
              >
                <MenuItem value={0}>Todos os projetos (global)</MenuItem>
                {loadingCompanies && <MenuItem disabled>Carregando projetos…</MenuItem>}
                {!loadingCompanies &&
                  companies.map((c) => (
                    <MenuItem key={c.id} value={Number(c.id)}>
                      {projectDisplayName(c)}
                    </MenuItem>
                  ))}
              </Select>
              <FormHelperText>Defina se esta forma é global ou específica de um projeto.</FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={Number(formData.status) === 1 ? 1 : 0}
                label="Status"
                onChange={handleChange}
                required
              >
                <MenuItem value={1}>Ativo</MenuItem>
                <MenuItem value={0}>Inativo</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth required>
              <InputLabel>Tipo</InputLabel>
              <Select
                name="tipo"
                value={Number(formData.tipo)}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    tipo: Number(e.target.value),
                    client_id: "",
                    client_secret: "",
                    urlapi: "",
                    pix_auth_url: "",
                    // limpa TLS ao trocar de tipo
                    pix_tls_mode: "",
                    pix_cert_pem: "",
                    pix_key_pem: "",
                    pix_ca_pem: "",
                    pix_pfx_b64: "",
                    pix_pfx_pass: "",
                  }))
                }
                label="Tipo"
              >
                {TIPO_OPCOES.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Switch Default */}
          <Grid item xs={12} md={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={Number(formData.default) === 1}
                  onChange={(_, checked) =>
                    setFormData((p) => ({ ...p, default: checked ? 1 : 0 }))
                  }
                />
              }
              label="Default"
            />
          </Grid>

          {/* Switch Emite BPe */}
          <Grid item xs={12} md={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={Number(formData.emitirbpe) === 1}
                  onChange={(_, checked) =>
                    setFormData((p) => ({ ...p, emitirbpe: checked ? 1 : 0 }))
                  }
                />
              }
              label="Emite BPe"
            />
            <FormHelperText sx={{ mt: -0.5 }}>
              Ative se esta forma de pagamento deve ser usada na emissão de BPe no fluxo de passagens.
            </FormHelperText>
          </Grid>

          {/* Id interno */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Id Interno"
              name="id_interno"
              value={formData.id_interno || ""}
              onChange={handleChange}
              fullWidth
              helperText="Código interno para integrações/relatórios (opcional)."
            />
          </Grid>

          {/* Valores e taxas */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Acréscimo (%)"
              name="acrescimo"
              type="number"
              value={formData.acrescimo ?? ""}
              onChange={handleChange}
              fullWidth
              inputProps={{ step: "0.01" }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Taxa de retenção (%) em estorno"
              name="taxa_retencao"
              type="number"
              value={formData.taxa_retencao ?? ""}
              onChange={handleChange}
              fullWidth
              inputProps={{ step: "0.01" }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Desconto (%)"
              name="desconto"
              type="number"
              value={formData.desconto ?? ""}
              onChange={handleChange}
              fullWidth
              inputProps={{ step: "0.01" }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Parcelas sem juros"
              name="parcelas_semjuros"
              type="number"
              value={formData.parcelas_semjuros ?? ""}
              onChange={handleChange}
              fullWidth
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Parcelas com juros"
              name="parcelas_comjuros"
              type="number"
              value={formData.parcelas_comjuros ?? ""}
              onChange={handleChange}
              fullWidth
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Juros do parcelamento (%)"
              name="juros_parcelamento"
              type="number"
              value={formData.juros_parcelamento ?? ""}
              onChange={handleChange}
              fullWidth
              inputProps={{ step: "0.01" }}
            />
          </Grid>

          {/* ======= Mapeamento de tipos por status do pagamento ======= */}
          {cascadeBases.length > 0 && (
            <>
              {cascadeBases.map((base) => renderCascade(base))}
              <Divider sx={{ width: "100%", my: 2 }} />
            </>
          )}

          {/* Campos por tipo */}
          {rules.show.has("client_id") && (
            <Grid item xs={12} md={6}>
              {(() => {
                const hasStored = hasStoredSecret("client_id");
                const isMasked = hasStored && !formData.client_id;
                return (
              <TextField
                label={labelFor("client_id", "Client ID")}
                name="client_id"
                    value={isMasked ? "••••••••" : formData.client_id}
                    onChange={isMasked ? undefined : handleChange}
                    onClick={isMasked ? () => maskSecretField("client_id") : undefined}
                    onFocus={isMasked ? () => maskSecretField("client_id") : undefined}
                fullWidth
                required={isFieldRequired("client_id")}
                helperText={
                      isMasked
                        ? "Valor já configurado. Clique para alterar."
                        : !formData.client_id && hasStoredSecret("client_id")
                    ? "Já existe um Client ID configurado. Preencha apenas se quiser trocar."
                    : undefined
                }
                    InputProps={isMasked ? { readOnly: true } : undefined}
              />
                );
              })()}
            </Grid>
          )}

          {rules.show.has("client_secret") && (
            <Grid item xs={12} md={6}>
              {(() => {
                const hasStored = hasStoredSecret("client_secret");
                const isMasked = hasStored && !formData.client_secret;
                return (
              <TextField
                label={labelFor("client_secret", "Client Secret")}
                name="client_secret"
                    value={isMasked ? "••••••••" : formData.client_secret}
                    onChange={isMasked ? undefined : handleChange}
                fullWidth
                type="password"
                autoComplete="new-password"
                required={isFieldRequired("client_secret")}
                helperText={
                      isMasked
                        ? "Valor já configurado. Clique para alterar."
                        : !formData.client_secret && hasStoredSecret("client_secret")
                    ? "Já existe um Client Secret configurado. Preencha apenas se quiser trocar."
                    : undefined
                }
                    InputProps={isMasked ? { readOnly: true } : undefined}
                    onClick={isMasked ? () => maskSecretField("client_secret") : undefined}
                    onFocus={isMasked ? () => maskSecretField("client_secret") : undefined}
              />
                );
              })()}
            </Grid>
          )}

          {rules.show.has("pix_key") && (
            <Grid item xs={12}>
              {(() => {
                const hasStored = hasStoredSecret("pix_key");
                const isMasked = hasStored && !formData.pix_key;
                return (
              <TextField
                label={labelFor("pix_key", "Pix Key")}
                name="pix_key"
                    value={isMasked ? "••••••••" : formData.pix_key}
                    onChange={isMasked ? undefined : handleChange}
                fullWidth
                required={isFieldRequired("pix_key")}
                helperText={
                      isMasked
                        ? "Valor já configurado. Clique para alterar."
                        : !formData.pix_key && hasStoredSecret("pix_key")
                    ? "Já existe uma Pix Key configurada. Preencha apenas se quiser trocar."
                    : undefined
                }
                    InputProps={isMasked ? { readOnly: true } : undefined}
                    onClick={isMasked ? () => maskSecretField("pix_key") : undefined}
                    onFocus={isMasked ? () => maskSecretField("pix_key") : undefined}
              />
                );
              })()}
            </Grid>
          )}

          {rules.show.has("pix_merchant_name") && (
            <Grid item xs={12}>
              <TextField
                label={labelFor("pix_merchant_name", "Pix Merchant Name")}
                name="pix_merchant_name"
                value={formData.pix_merchant_name}
                onChange={handleChange}
                fullWidth
                required={rules.required.includes("pix_merchant_name")}
              />
            </Grid>
          )}

          {rules.show.has("pix_merchant_city") && (
            <Grid item xs={12}>
              <TextField
                label={labelFor("pix_merchant_city", "Pix Merchant City")}
                name="pix_merchant_city"
                value={formData.pix_merchant_city}
                onChange={handleChange}
                fullWidth
                required={rules.required.includes("pix_merchant_city")}
              />
            </Grid>
          )}

          {/* URL base (Cielo / PIX Itaú / Adyen / AppMax) */}
          {rules.show.has("urlapi") && (
            <Grid item xs={12}>
              <TextField
                label={labelFor("urlapi", "URL da API")}
                name="urlapi"
                value={formData.urlapi}
                onChange={handleChange}
                fullWidth
                helperText={rules.helpers?.urlapi}
              />
            </Grid>
          )}

          {rules.show.has("pix_auth_url") && (
            <Grid item xs={12}>
              <TextField
                label={labelFor("pix_auth_url", "URL do OAuth (opcional)")}
                name="pix_auth_url"
                value={formData.pix_auth_url}
                onChange={handleChange}
                fullWidth
                helperText="Se vazio, o backend tentará /api/oauth/token e /oauth/token no host acima."
              />
            </Grid>
          )}

          {/* ===== TLS PIX ITAÚ ===== */}
          {isPixItau && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Autenticação mTLS (PIX Itaú)
                </Typography>

                <RadioGroup
                  row
                  name="pix_tls_mode"
                  value={formData.pix_tls_mode || ""}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      pix_tls_mode: e.target.value,
                      // limpa pares ao trocar de modo
                      pix_cert_pem: "",
                      pix_key_pem: "",
                      pix_ca_pem: "",
                      pix_pfx_b64: "",
                      pix_pfx_pass: "",
                    }))
                  }
                >
                  <FormControlLabel value="pem" control={<Radio />} label="PEM (Cert + Key)" />
                  <FormControlLabel value="pfx" control={<Radio />} label="PFX/P12 (arquivo + senha)" />
                </RadioGroup>

                {formData.pix_tls_mode === "pem" && (
                  <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                      label="Cert PEM (-----BEGIN CERTIFICATE-----)"
                      name="pix_cert_pem"
                      value={
                          hasStoredSecret("pix_cert_pem") && !formData.pix_cert_pem
                            ? "••••••••"
                            : formData.pix_cert_pem
                        }
                        onChange={
                          hasStoredSecret("pix_cert_pem") && !formData.pix_cert_pem
                            ? undefined
                            : (e) => setFormData((p) => ({ ...p, pix_cert_pem: e.target.value }))
                        }
                      fullWidth
                      multiline
                      minRows={6}
                      helperText={
                            hasStoredSecret("pix_cert_pem") && !formData.pix_cert_pem
                              ? "Valor já configurado. Clique para alterar."
                              : !formData.pix_cert_pem && hasStoredSecret("pix_cert_pem")
                          ? "Já existe um Cert PEM configurado. Preencha apenas se quiser trocar."
                          : undefined
                      }
                        InputProps={
                          hasStoredSecret("pix_cert_pem") && !formData.pix_cert_pem
                            ? { readOnly: true }
                            : undefined
                        }
                        onClick={
                          hasStoredSecret("pix_cert_pem") && !formData.pix_cert_pem
                            ? () => maskSecretField("pix_cert_pem")
                            : undefined
                        }
                        onFocus={
                          hasStoredSecret("pix_cert_pem") && !formData.pix_cert_pem
                            ? () => maskSecretField("pix_cert_pem")
                            : undefined
                        }
                    />
                    <TextField
                      label="Key PEM (-----BEGIN PRIVATE KEY-----)"
                      name="pix_key_pem"
                      value={
                          hasStoredSecret("pix_key_pem") && !formData.pix_key_pem
                            ? "••••••••"
                            : formData.pix_key_pem
                        }
                        onChange={
                          hasStoredSecret("pix_key_pem") && !formData.pix_key_pem
                            ? undefined
                            : (e) => setFormData((p) => ({ ...p, pix_key_pem: e.target.value }))
                        }
                      fullWidth
                      multiline
                      minRows={6}
                      helperText={
                            hasStoredSecret("pix_key_pem") && !formData.pix_key_pem
                              ? "Valor já configurado. Clique para alterar."
                              : !formData.pix_key_pem && hasStoredSecret("pix_key_pem")
                          ? "Já existe uma Key PEM configurada. Preencha apenas se quiser trocar."
                          : undefined
                      }
                        InputProps={
                          hasStoredSecret("pix_key_pem") && !formData.pix_key_pem
                            ? { readOnly: true }
                            : undefined
                        }
                        onClick={
                          hasStoredSecret("pix_key_pem") && !formData.pix_key_pem
                            ? () => maskSecretField("pix_key_pem")
                            : undefined
                        }
                        onFocus={
                          hasStoredSecret("pix_key_pem") && !formData.pix_key_pem
                            ? () => maskSecretField("pix_key_pem")
                            : undefined
                        }
                    />
                    <TextField
                      label="CA PEM (opcional – cadeia intermediária)"
                      name="pix_ca_pem"
                      value={formData.pix_ca_pem}
                      onChange={(e) => setFormData((p) => ({ ...p, pix_ca_pem: e.target.value }))}
                      fullWidth
                      multiline
                      minRows={4}
                    />
                  </Stack>
                )}

                {formData.pix_tls_mode === "pfx" && (
                  <Stack spacing={2} sx={{ mt: 1 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Button component="label" variant="outlined">
                        Selecionar PFX/P12
                        <input
                          type="file"
                          accept=".pfx,.p12,application/x-pkcs12"
                          hidden
                          onChange={async (e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            const b64 = await handleFileToBase64(f);
                            setFormData((p) => ({ ...p, pix_pfx_b64: b64 }));
                          }}
                        />
                      </Button>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {formData.pix_pfx_b64 ? "Arquivo carregado ✔" : "Nenhum arquivo selecionado"}
                      </Typography>
                    </Stack>
                    <TextField
                      label="Senha do PFX"
                      name="pix_pfx_pass"
                      value={
                          hasStoredSecret("pix_pfx_pass") && !formData.pix_pfx_pass
                            ? "••••••••"
                            : formData.pix_pfx_pass
                        }
                        onChange={
                          hasStoredSecret("pix_pfx_pass") && !formData.pix_pfx_pass
                            ? undefined
                            : (e) => setFormData((p) => ({ ...p, pix_pfx_pass: e.target.value }))
                        }
                      fullWidth
                      type="password"
                      autoComplete="new-password"
                      helperText={
                            hasStoredSecret("pix_pfx_pass") && !formData.pix_pfx_pass
                              ? "Valor já configurado. Clique para alterar."
                              : !formData.pix_pfx_pass && hasStoredSecret("pix_pfx_pass")
                          ? "Já existe uma senha de PFX configurada. Preencha apenas se quiser trocar."
                          : undefined
                      }
                        InputProps={
                          hasStoredSecret("pix_pfx_pass") && !formData.pix_pfx_pass
                            ? { readOnly: true }
                            : undefined
                        }
                        onClick={
                          hasStoredSecret("pix_pfx_pass") && !formData.pix_pfx_pass
                            ? () => maskSecretField("pix_pfx_pass")
                            : undefined
                        }
                        onFocus={
                          hasStoredSecret("pix_pfx_pass") && !formData.pix_pfx_pass
                            ? () => maskSecretField("pix_pfx_pass")
                            : undefined
                        }
                    />
                    <TextField
                      label="CA PEM (opcional – cadeia intermediária)"
                      name="pix_ca_pem"
                      value={formData.pix_ca_pem}
                      onChange={(e) => setFormData((p) => ({ ...p, pix_ca_pem: e.target.value }))}
                      fullWidth
                      multiline
                      minRows={4}
                    />
                  </Stack>
                )}
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Rodapé */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={onClose} disabled={isSubmitting || isLoading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={!canSubmit}>
            {isSubmitting || isLoading ? <CircularProgress size={20} /> : "Salvar"}
          </Button>
        </Box>

        {/* Exibição agregada de erros (opcional) */}
        {errors.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning">
              {errors.map((e, i) => (
                <div key={i}>{e}</div>
              ))}
            </Alert>
          </Box>
        )}
      </form>
    </Paper>
  );
};

export default AddPaymentMethodForm;
